import React from 'react';
import {StyleSheet, View} from 'react-native';
import CCText from '@/Atoms/CCText';
import Box from '@/Atoms/Box';
import CCLoader from '@/Atoms/CCLoader';
import {useGetPlayerAssessmentApi} from '@/Hooks/PlayerProfileApi/useGetPlayerAssessmentApi';
import ProfileAsset from '../../ui/ProfileAsset';
import ProfileButton from '../../ui/ProfileButton';
import ThunderText from '../../ui/ThunderText';

const styles = StyleSheet.create({
  // Spec §2 card shell: radius 20, padding 24/26, soft shadow, no border.
  card: {
    backgroundColor: '#fff',
    borderRadius: 20,
    paddingVertical: 24,
    paddingHorizontal: 26,
    marginBottom: 18,
    shadowColor: 'rgba(77,63,55,1)',
    shadowOffset: {width: 0, height: 2},
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 2,
  },
  runButton: {
    marginLeft: 'auto',
  },
  heroBanner: {
    backgroundColor: '#FACF47',
    borderRadius: 16,
    padding: 18,
    marginBottom: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  heroIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    backgroundColor: 'rgba(255,255,255,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroTextCol: {
    flex: 1,
    minWidth: 160,
  },
  // Banner is a row: icon + copy on the left, the Run assessment CTA pushed
  // to the right (wraps beneath on a narrow card).
  heroRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
  },
  heroEyebrow: {
    fontSize: 11,
    fontWeight: '700',
    color: '#6B5717',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  heroSubtitle: {
    fontSize: 12,
    color: '#6B5717',
    marginTop: 2,
  },
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  areaCard: {
    backgroundColor: '#FAF6EB',
    borderRadius: 14,
    padding: 14,
    minWidth: 200,
    flexGrow: 1,
    flexBasis: '30%',
  },
  areaTopRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
  },
  areaName: {
    fontSize: 13,
    fontWeight: '600',
    color: '#262322',
    flexShrink: 1,
  },
  levelMeter: {
    flexDirection: 'row',
    gap: 3,
    marginTop: 12,
    marginBottom: 8,
  },
  levelSegment: {
    flex: 1,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#E0DDD9',
  },
  // Tier-colored caption ("Level 4 · Strong") -- color set inline per tier.
  areaCaption: {
    fontSize: 11,
    fontWeight: '600',
  },
  areaMeta: {
    fontSize: 11,
    color: '#888280',
    marginTop: 12,
  },
  emptyText: {
    fontSize: 12,
    color: '#888280',
  },
});

const MAX_LEVEL = 5;
// Presentational tier labels for the 1-5 `assessment_level` scale -- not a
// backend field, just a copy layer over the real number (matches the
// design's "Level 4: Strong" style captions).
const LEVEL_LABELS: Record<number, string> = {
  1: 'Emerging',
  2: 'Developing',
  3: 'Solid',
  4: 'Strong',
  5: 'Expert',
};

// Rating numeral, level meter, and caption are colored by the level tier
// (design: teal high → gold → orange → red low).
const tierColor = (level: number) => {
  if (level >= 4) {
    return '#3DAB9E';
  }
  if (level === 3) {
    return '#D6A420';
  }
  if (level === 2) {
    return '#E17846';
  }
  return '#CD2C39';
};

type Props = {
  username?: string;
  navigation?: any;
};

// Owner-only tab, same rationale as Training.
export default function AssessmentTab({username, navigation}: Props) {
  const {data, isLoading, isError} = useGetPlayerAssessmentApi(username);

  if (isLoading) {
    return (
      <Box style={styles.card}>
        <CCLoader loading={true} isComponentLoader />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box style={styles.card}>
        <CCText style={styles.emptyText}>Couldn't load assessment.</CCText>
      </Box>
    );
  }

  const areaCodes = Object.keys(data.area_labels);
  // Areas below Level 3, weakest first -- drives the "N areas need work" banner
  // and names the specific lowest areas in the subtitle.
  const lowAreas = areaCodes
    .map(code => ({
      code,
      level: data.areas[code]?.stats?.[0]?.assessment_level,
    }))
    .filter(
      (a): a is {code: string; level: number} =>
        a.level != null && a.level <= 2,
    )
    .sort((a, b) => a.level - b.level);
  const needsWorkCount = lowAreas.length;
  const lowestNames = lowAreas.slice(0, 2).map(a => data.area_labels[a.code]);
  const lowestSubtitle =
    lowestNames.length > 0
      ? `${lowestNames.join(' and ')} ${
          lowestNames.length > 1 ? 'are' : 'is'
        } your lowest level${lowestNames.length > 1 ? 's' : ''}.`
      : '';

  // Headline in the banner: what's running / needs work / on track.
  const headline = data.is_running
    ? 'Assessment running…'
    : needsWorkCount > 0
    ? `${needsWorkCount} area${needsWorkCount === 1 ? '' : 's'} need work`
    : 'You’re on track';
  const subtitle = data.is_running ? '' : lowestSubtitle;

  return (
    <Box style={styles.card}>
      <View style={[styles.heroBanner, styles.heroRow]}>
        <View style={styles.heroIcon}>
          <ProfileAsset name="assessment" size={30} />
        </View>
        <View style={styles.heroTextCol}>
          <CCText style={styles.heroEyebrow}>Assessment</CCText>
          <ThunderText size={30} lineHeight={32} color="#262322">
            {headline}
          </ThunderText>
          {subtitle ? (
            <CCText style={styles.heroSubtitle}>{subtitle}</CCText>
          ) : null}
        </View>
        <ProfileButton
          variant="dark"
          testID="assessment-run-cta"
          disabled={data.is_running}
          onPress={() => navigation?.navigate?.('AssessmentReport')}
          style={styles.runButton}>
          {data.is_running ? 'Running…' : 'Run assessment'}
        </ProfileButton>
      </View>

      <View style={styles.grid}>
        {areaCodes.map(code => {
          const area = data.areas[code];
          const latest = area?.stats?.[0];
          const color = latest ? tierColor(latest.assessment_level) : '#888280';
          return (
            <View key={code} style={styles.areaCard}>
              <View style={styles.areaTopRow}>
                <CCText style={styles.areaName} numberOfLines={2}>
                  {data.area_labels[code]}
                </CCText>
                {latest ? (
                  <ThunderText size={26} lineHeight={26} color={color}>
                    {latest.assessment_rating.toFixed(1)}
                  </ThunderText>
                ) : null}
              </View>
              {latest ? (
                <>
                  <View style={styles.levelMeter}>
                    {Array.from({length: MAX_LEVEL}).map((_, idx) => (
                      <View
                        key={idx}
                        style={[
                          styles.levelSegment,
                          idx < latest.assessment_level && {
                            backgroundColor: color,
                          },
                        ]}
                      />
                    ))}
                  </View>
                  <CCText style={[styles.areaCaption, {color}]}>
                    Level {latest.assessment_level} ·{' '}
                    {LEVEL_LABELS[latest.assessment_level] ?? ''}
                  </CCText>
                </>
              ) : (
                <CCText style={styles.areaMeta}>No data yet</CCText>
              )}
            </View>
          );
        })}
      </View>
    </Box>
  );
}
